import React, { useState } from 'react'
import { Tooltip, Typography, makeStyles } from '@material-ui/core'
import PetsIcon from '@material-ui/icons/Pets'
import { SvgIconProps } from '@material-ui/core/SvgIcon'

import { LeasePet, formatPetLabel, getLeasePetTooltip } from '../models'
import { DOG_BREEDS } from '../containers/LeasingSeasonConfig/petPolicy'

const BREED_LABEL: Record<string, string> = {}
for (const row of DOG_BREEDS) {
    BREED_LABEL[row.id] = row.label
}

const useStyles = makeStyles({
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 160,
        maxWidth: 240,
    },
    pet: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    photo: {
        width: '100%',
        maxHeight: 180,
        objectFit: 'cover',
        borderRadius: 8,
        display: 'block',
        backgroundColor: '#111827',
    },
    label: {
        fontWeight: 700,
        fontSize: '0.8125rem',
        lineHeight: 1.3,
    },
    detail: {
        fontSize: '0.75rem',
        lineHeight: 1.35,
        opacity: 0.92,
    },
    wrap: {
        display: 'inline-flex',
        flexShrink: 0,
        alignItems: 'center',
        lineHeight: 0,
    },
})

function breedLabel(breed: string | undefined): string {
    const id = (breed || '').trim()
    if (!id) return ''
    if (BREED_LABEL[id]) return BREED_LABEL[id]
    return id
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

function detailLines(pet: LeasePet): string[] {
    const lines: string[] = []
    const breed = breedLabel(pet.breed)
    if (breed) lines.push(breed)
    const weight = (pet.weight || '').trim()
    if (weight) lines.push(`${weight} lb`)
    const notes = (pet.notes || '').trim()
    if (notes) lines.push(notes)
    return lines
}

export function PetTooltipContent({ pets }: { pets: LeasePet[] }) {
    const classes = useStyles()
    return (
        <div className={classes.card}>
            {pets.map((pet, index) => (
                <PetCard
                    key={`${pet.name}-${pet.animal_type}-${index}`}
                    pet={pet}
                />
            ))}
        </div>
    )
}

function PetCard({ pet }: { pet: LeasePet }) {
    const classes = useStyles()
    const [broken, setBroken] = useState(false)
    const pictureUrl = (pet.picture_url || '').trim()
    const lines = detailLines(pet)
    return (
        <div className={classes.pet}>
            {pictureUrl && !broken && (
                <img
                    src={pictureUrl}
                    alt={formatPetLabel(pet)}
                    className={classes.photo}
                    onError={() => setBroken(true)}
                />
            )}
            <Typography className={classes.label}>
                {formatPetLabel(pet)}
            </Typography>
            {lines.map((line) => (
                <Typography key={line} className={classes.detail}>
                    {line}
                </Typography>
            ))}
        </div>
    )
}

export function PetHoverIcon({
    pets,
    className,
    fontSize = 'small',
    htmlColor = '#8A8A8A',
    style,
}: {
    pets?: LeasePet[] | null
    className?: string
    fontSize?: SvgIconProps['fontSize']
    htmlColor?: string
    style?: React.CSSProperties
}) {
    const classes = useStyles()
    if (!pets || pets.length === 0) {
        return null
    }
    const aria = getLeasePetTooltip(pets)
    return (
        <Tooltip
            interactive
            enterDelay={200}
            placement="right"
            title={<PetTooltipContent pets={pets} />}
        >
            <span
                className={classes.wrap}
                onClick={(event) => event.stopPropagation()}
            >
                <PetsIcon
                    className={className}
                    fontSize={fontSize}
                    htmlColor={htmlColor}
                    aria-label={aria}
                    style={style}
                />
            </span>
        </Tooltip>
    )
}

export function LeasePetMarker({
    pets,
    hasPet,
    className,
    fontSize = 'small',
    htmlColor = '#8A8A8A',
    style,
}: {
    pets?: LeasePet[] | null
    hasPet?: boolean
    className?: string
    fontSize?: SvgIconProps['fontSize']
    htmlColor?: string
    style?: React.CSSProperties
}) {
    if (pets && pets.length > 0) {
        return (
            <PetHoverIcon
                pets={pets}
                className={className}
                fontSize={fontSize}
                htmlColor={htmlColor}
                style={style}
            />
        )
    }
    if (!hasPet) return null
    return (
        <Tooltip title="Has a pet">
            <span
                style={{ display: 'inline-flex', lineHeight: 0 }}
                onClick={(event) => event.stopPropagation()}
            >
                <PetsIcon
                    className={className}
                    fontSize={fontSize}
                    htmlColor={htmlColor}
                    aria-label="Has a pet"
                    style={style}
                />
            </span>
        </Tooltip>
    )
}
